import { useMachine } from '@xstate/react';
import { useToaster } from 'design-system';
import { useCallback } from 'react';

import { AUTH_TOKEN_KEY } from './constants';
import { createLoginLogoutMachine } from './login-logout-machine';
import { GetSsoToken } from './sso';
import { UserState } from './user-state';

export type LoginLogoutState = {
  error: unknown;
  name: 'logging in' | 'logged in' | 'logged out';
  logIn: (
    credentials: { email: string; password: string },
    callback?: (userId: number, token: string) => void,
  ) => void;
  logOut: () => void;
};

export function useLoginLogout({
  userState,
  getSsoToken,
  on,
}: {
  userState: UserState;
  getSsoToken: (
    params: Pick<Parameters<GetSsoToken>[0], 'credentials'>,
  ) => ReturnType<GetSsoToken>;
  on: {
    loggedIn: () => void;
    loggedOut: () => void;
  };
}): LoginLogoutState {
  const { showToast } = useToaster();
  const [state, send] = useMachine(() =>
    createLoginLogoutMachine({
      getSsoToken,
      notifyOfLoggedIn: () => {
        on.loggedIn();
        userState.invalidate();

        showToast({
          color: 'success',
          title: 'You are now logged in',
        });
      },
      notifyOfLoggedOut: () => {
        userState.invalidate();
        on.loggedOut();

        showToast({
          color: 'danger',
          title: 'You are now logged out',
        });
      },
      removeSsoTokenFromLocalStorage: () => {
        localStorage.removeItem(AUTH_TOKEN_KEY);
        localStorage.removeItem('ssoUserId');
        localStorage.removeItem('campaign-notification-seen');
      },
      storeSsoTokenInLocalStorage: ({ ssoUserId, token }) => {
        console.info(`Storing SSO token for user ${ssoUserId}`);
        localStorage.setItem('ssoUserId', ssoUserId.toString());
        localStorage.setItem(AUTH_TOKEN_KEY, token);
        localStorage.setItem('visited', 'visited');
      },
    }),
  );

  const error = state.context.error;
  const name = state.matches('logging in')
    ? 'logging in'
    : state.matches('logged in')
    ? 'logged in'
    : 'logged out';
  const logIn = useCallback<
    (
      credentials: { email: string; password: string },
      callback?: (userId: number, token: string) => void,
    ) => void
  >(
    (credentials, callback) => {
      send({ type: 'log in', credentials, callback });
    },
    [send],
  );
  const logOut = useCallback(() => {
    send({ type: 'log out' });
  }, [send]);

  return { error, name, logIn, logOut };
}
